import { useEffect, useState, useCallback } from 'react';
import { Box, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { getActiveCharts } from '@/actions/chart';
import { useApi } from '@/providers/ApiContext';
import type { Chart, EntityId } from '@/types';
import debounce from 'lodash/debounce';

interface PlotChartFieldProps {
  document_type_id?: EntityId;
  value: Chart | null;
  setValue: (value: Chart | null) => void;
  setChanged: () => void;
  edit?: string | boolean;
  error: string | boolean;
  setError: (error: string | boolean) => void;
}

function PlotChartField({
  document_type_id,
  value,
  setValue,
  setChanged,
  edit,
  error,
  setError,
}: PlotChartFieldProps): React.JSX.Element {
  const api = useApi();
  const [query, setQuery] = useState<string>('');
  const [charts, setCharts] = useState<Chart[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selected, setSelected] = useState<Chart | null>(value);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [isOpen, setIsOpen] = useState<boolean>(false);

  // Update selected when value prop changes (for pre-selected values)
  useEffect(() => {
    setSelected(value);
  }, [value]);

  const fetchCharts = useCallback(
    (newQuery: string, startKey: string | null = null) => {
      if (!document_type_id || !api) return;
      debounce(async () => {
        setLoading(true);
        try {
          const response = await getActiveCharts(
            api,
            document_type_id,
            20,
            startKey || undefined,
            Boolean(newQuery) ? newQuery : undefined,
          );
          setCharts((prevOptions) =>
            startKey === null
              ? response.results
              : [...(prevOptions || []), ...response.results],
          );
          setHasNextPage(response.last_key !== null);
          if (response.last_key) {
            setCursors((prev) => ({
              ...prev,
              [Object.keys(prev).length + 1]: response.last_key!,
            }));
          }
        } catch (error) {
          console.log(error);
          setCharts([]);
        } finally {
          setLoading(false);
        }
      }, 300)();
    },
    [setLoading, api, document_type_id],
  );

  useEffect(() => {
    if (isOpen && !charts && !edit) {
      setCursors({});
      fetchCharts(''); // Fetch initial items when dropdown opens
    }
  }, [isOpen, charts, fetchCharts, edit]);

  useEffect(() => {
    if (query.length >= 1 && !edit) {
      setCursors({});
      fetchCharts(query); // Fetch new items for new query
    } else if (query.length === 0 && isOpen && !edit) {
      setCursors({});
      fetchCharts(''); // Reset to initial items when query is cleared
    }
  }, [query, fetchCharts, isOpen, edit]);

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollHeight - listboxNode.scrollTop ===
        listboxNode.clientHeight &&
      !loading &&
      hasNextPage
    ) {
      const nextCursor = cursors[Object.keys(cursors).length];
      fetchCharts(query, nextCursor);
    }
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: Chart | null,
  ) => {
    setChanged();
    setSelected(newValue);
    setValue(newValue);
    if (setError) {
      setError(false); // Clear any existing error when user makes a selection
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '600px',
        justifyContent: 'center',
        justifyItems: 'center',
        textAlign: 'justify',
      }}
    >
      <Autocomplete
        id='chart-field'
        options={charts || []}
        loading={loading}
        sx={{ width: '100%' }}
        onInputChange={(_event, newInputValue) => setQuery(newInputValue)}
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          setIsOpen(false);
          setCharts(null);
        }}
        slotProps={{
          listbox: {
            onScroll: handleScroll,
          },
        }}
        value={selected}
        onChange={handleChange}
        disabled={Boolean(edit)}
        getOptionLabel={(option) => (option ? `${option.name}` : '')}
        disableClearable={false}
        isOptionEqualToValue={(option, value) =>
          option?.entity_id === value?.entity_id
        }
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label='Select Chart'
            placeholder={
              !selected ? 'Click to load chart list or start typing...' : ''
            }
            error={Boolean(error)}
            helperText={
              error ||
              'Select the chart for this plot. Charts define the structure and requirements for the plot. You can also type to search faster.'
            }
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color='inherit' size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
}

export default PlotChartField;

